// Fiche d'un besoin de quartier (M17, lot L5-C1, D-250) : origine, priorité, qualification, orientation vers
// un signalement ou un projet (lue dans son module, détaillée seulement avec le droit de ce domaine),
// traitement ou refus motivé. Les actes dépendent de l'état et des droits du domaine cible.
import { CATEGORIES_BESOIN, ORIGINES_BESOIN, PRIORITES_BESOIN } from '@cc/contrats';
import { Bouton, Lien, PuceEtat, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { Fenetre } from '../achats/Fenetre';
import { INVALIDER_PARTICIPATION, rejeter, traiter, useBesoin } from './api';
import { FormulaireOrientation, FormulaireQualification } from './FormulairesProximite';
import { actesBesoin, libelleBesoin, texteOrientation, TON_BESOIN, type ActeBesoin } from './logique';
import styles from '../achats/achats.module.css';

const LIBELLES: Record<ActeBesoin, string> = {
  qualifier: 'Qualifier', rejeter: 'Ne pas retenir', orienter_signalement: 'Orienter vers un signalement', orienter_projet: 'Orienter vers un projet', traiter: 'Déclarer traité',
};

function FormulaireMotif({ titre, executer, onFermer }: { titre: string; executer: (motif: string) => Promise<unknown>; onFermer: () => void }) {
  const [motif, setMotif] = useState('');
  const action = useAction({ executer: () => executer(motif), succes: 'Besoin mis à jour ; le représentant auteur est informé.', invalider: INVALIDER_PARTICIPATION, apres: onFermer });
  return (
    <Fenetre titre={titre} description="Le texte est communiqué au représentant auteur du besoin." libelle="Enregistrer" enCours={action.enCours} onFermer={onFermer} onValider={() => action.lancer(undefined)} erreur={action.messageErreur}>
      <ZoneTexte libelle="Motif ou observation" value={motif} onChange={(e) => setMotif(e.target.value)} required />
    </Fenetre>
  );
}

export function PageBesoin() {
  const { id } = useParams();
  const { contexte } = useSession();
  const besoin = useBesoin(id);
  const [acte, setActe] = useState<ActeBesoin | null>(null);
  const b = besoin.data;
  const actes = b ? actesBesoin(b, { modifier: peut(contexte, 'quartier', 'modifier'), signalement: peut(contexte, 'intervention', 'creer'), projet: peut(contexte, 'travaux', 'creer') }) : [];
  const fermer = () => setActe(null);
  return (
    <>
      <EnTetePage etiquette="Proximité" titre={b ? `${b.numero} : ${b.titre}` : 'Besoin'} description={b ? `Quartier ${b.quartier.nom} · ${ORIGINES_BESOIN[b.origine]}${b.exprime_par ? ` (${b.exprime_par})` : ''} · le ${dateHeure(b.cree_le)}` : undefined} actions={b ? <Lien href={`/proximite/quartiers/${b.quartier.id}`}>Quartier {b.quartier.nom}</Lien> : null} />
      <EtatRequete chargement={besoin.isPending} erreur={besoin.error} onReessayer={() => void besoin.refetch()} lot="L5-C1" fonction="La fiche du besoin">
        {b ? (
          <>
            <div className={styles.actes}>{actes.map((a) => <Bouton key={a} variante={a === 'rejeter' ? 'secondaire' : 'principal'} onClick={() => setActe(a)}>{LIBELLES[a]}</Bouton>)}</div>
            <section className={styles.bloc} aria-labelledby="titre-besoin">
              <h2 id="titre-besoin">Besoin</h2>
              <p><PuceEtat ton={TON_BESOIN[b.etat]} libelle={libelleBesoin(b.etat)} />{` ${CATEGORIES_BESOIN[b.categorie]} · ${PRIORITES_BESOIN[b.priorite]}${b.fictif ? ' · fictif' : ''}`}</p>
              <p>{b.description}</p>
              {b.adresse ? <p className={styles.detail}>{`Lieu : ${b.adresse}`}</p> : null}
              {b.observation ? <p className={styles.detail}>{`Observation : ${b.observation}`}</p> : null}
              {b.motif_rejet ? <p className={styles.detail}>{`Non retenu : ${b.motif_rejet}`}</p> : null}
            </section>
            <section className={styles.bloc} aria-labelledby="titre-orientation">
              <h2 id="titre-orientation">Orientation et suivi</h2>
              <p>{texteOrientation(b.orientation)}</p>
              {b.orientation?.visible && b.orientation.cible === 'signalement' ? <Lien href={`/interventions/signalements/${b.orientation.id}`}>Ouvrir le signalement</Lien> : null}
              {b.orientation?.visible && b.orientation.cible === 'projet_travaux' ? <Lien href={`/travaux/${b.orientation.id}`}>Ouvrir le projet</Lien> : null}
            </section>
          </>
        ) : null}
      </EtatRequete>
      {b && acte === 'qualifier' ? <FormulaireQualification besoin={b.id} onFermer={fermer} /> : null}
      {b && acte === 'orienter_signalement' ? <FormulaireOrientation besoin={b.id} cible="signalement" onFermer={fermer} /> : null}
      {b && acte === 'orienter_projet' ? <FormulaireOrientation besoin={b.id} cible="projet_travaux" onFermer={fermer} /> : null}
      {b && acte === 'rejeter' ? <FormulaireMotif titre="Ne pas retenir le besoin" executer={(m) => rejeter(b.id, m)} onFermer={fermer} /> : null}
      {b && acte === 'traiter' ? <FormulaireMotif titre="Déclarer le besoin traité" executer={(m) => traiter(b.id, m)} onFermer={fermer} /> : null}
    </>
  );
}
